import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import SidebarPanelButton from '../../../../components/common/SidebarPanelButton';
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  normalizeFontSize,
} from '../../../../utils/normalizeFontSize';

const TextFontSizeControl = ({
  disabled = false,
  displayValue,
  onBlur,
  onChange,
  onFocus,
  onStep,
  t,
}) => {
  const numericValue = useMemo(() => {
    if (displayValue === '' || displayValue == null) {
      return null;
    }
    const parsed = Number(displayValue);
    return Number.isNaN(parsed) ? null : parsed;
  }, [displayValue]);

  const resolvedSize = numericValue ?? FONT_SIZE_MIN;

  const canDecrease = !disabled && resolvedSize > FONT_SIZE_MIN;
  const canIncrease = !disabled && resolvedSize < FONT_SIZE_MAX;

  const handleStep = useCallback(
    (delta) => {
      if (disabled) {
        return;
      }
      onStep(delta);
    },
    [disabled, onStep],
  );

  const handleInputKeyDown = useCallback(
    (event) => {
      if (disabled) {
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        onStep(1);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        onStep(-1);
      }
    },
    [disabled, onStep],
  );

  return (
    <SidebarPanelButton
      className="respark_text-size-option respark_text-size-option--stepper-only"
      title={t('size')}
      disabled={disabled}
    >
      <div
        className="respark_text-size-stepper"
        role="group"
        aria-label={t('size')}
      >
        <button
          type="button"
          className="respark_text-size-stepper__btn respark_text-size-stepper__btn--decrease"
          disabled={!canDecrease}
          aria-label={t('decreaseFontSize')}
          onClick={() => handleStep(-1)}
        >
          −
        </button>
        <input
          className="respark_text-size-stepper__input"
          value={displayValue}
          name="fontSize"
          onFocus={onFocus}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={handleInputKeyDown}
          inputMode="numeric"
          type="number"
          min={FONT_SIZE_MIN}
          max={FONT_SIZE_MAX}
          step={1}
          placeholder={t('size')}
          disabled={disabled}
          aria-label={t('size')}
        />
        <button
          type="button"
          className="respark_text-size-stepper__btn respark_text-size-stepper__btn--increase"
          disabled={!canIncrease}
          aria-label={t('increaseFontSize')}
          onClick={() => handleStep(1)}
        >
          +
        </button>
      </div>
    </SidebarPanelButton>
  );
};

TextFontSizeControl.propTypes = {
  disabled: PropTypes.bool,
  displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onStep: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default TextFontSizeControl;
